import { FormLabel, TextField } from "@cmsgov/ds-medicare-gov";
import { ChangeEventHandler, MouseEventHandler, useEffect, useState } from "react";
import Div from "../../common/components/div";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import TranslatedSpan from "../../common/components/TranslatedSpan";
import translate from "../../common/functions/translate";
import { useConversion } from "../../common/hooks/use.analytics";
import { ForgotPasswordErrorsResponse, useForgotPasswordApi } from "../../common/hooks/useForgotPasswordApi";
import { Buttons, Step } from "./ForgotPassword";

interface AnswerSecretQuestionProps {
    combinedWithUsername: boolean
    conversion: ReturnType<typeof useConversion>
    secretQuestion: string
    formStart: () => void
    onBackClick: MouseEventHandler
    setStep: React.Dispatch<React.SetStateAction<number>>
    medicareNumber: string
    secretAnswer: string
    setSecretAnswer: React.Dispatch<React.SetStateAction<string>>
    enterInformationAlert?: boolean
}

type FieldErrState = 'empty' | 'invalid' | null;

export const AnswerSecretQuestion = (props: AnswerSecretQuestionProps) => {
    const { conversion, formStart, setStep, medicareNumber, secretQuestion, secretAnswer, setSecretAnswer, enterInformationAlert } = props;
    const [step2ApiErrResponse, setStep2ApiErrResponse] = useState<ForgotPasswordErrorsResponse | null>(null);
    const [answerFailCount, setAnswerFailCount] = useState(0);
    const [secretAnswerErrState, setSecretAnswerErrState] = useState<FieldErrState>(null);
    const [secretAnswerTouched, setSecretAnswerTouched] = useState(false);
    const [submit2Attempted, setSubmit2Attempted] = useState(false);
    const [answerQuestionLoading, setAnswerQuestionLoading] = useState(false);

    const answerHeader = translate('answer_secret_question');
    const forgotPasswordApi = useForgotPasswordApi();

    useEffect(() => {
        setSecretAnswerErrState(secretAnswer.length ? null : 'empty');
    }, [secretAnswer]);

    const getSecretAnswerErrMessage = () => {
        if (!secretAnswerTouched && !submit2Attempted) {
            return null;
        }
        return secretAnswerErrState ? translate('enter_answer_to_your_secret_question') : null;
    }

    const onSecretAnswerChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        formStart();
        const newAns = event.target.value;
        setSecretAnswer(newAns);
    };

    const prev = () => {
        setStep(i => i - 1);
    };

    const next = () => {
        setStep(i => i + 1);
    };

    const submitSecretAnswer = () => {
        return forgotPasswordApi.answerSecretQuestion(medicareNumber, secretQuestion, secretAnswer);
    };

    const secretQuestionSubmit = async () => {
        setSubmit2Attempted(true);
        conversion.attempt();
        if (secretAnswerErrState) {
            // manually trigger conversion failure because this step doesn't show an error alert for validation errors
            conversion.failValidationIntermediate('Enter the answer to your secret question');
            return;
        }

        try {
            setStep2ApiErrResponse(null);
            setAnswerQuestionLoading(true);
            await submitSecretAnswer();
            next();
        } catch (e: any) {
            setStep2ApiErrResponse(e);
            if (e.code === 403) {
                setAnswerFailCount(lastCount => lastCount + 1);
            }
        } finally {
            setAnswerQuestionLoading(false);
        }
    };

    const title = enterInformationAlert ? translate("make_sure_its_you") : undefined;
    return (
        <Step stepNum={2} stepTitle={translate('answer_secret_question')} title={title} combinedWithUsername={props.combinedWithUsername} allFieldsRequired>
            <SmartErrorAlert
                name="ForgotPassword2"
                conversion={conversion}
                apiResponse={step2ApiErrResponse}
                incorrectAnswerCount={answerFailCount}
                parentHeading={answerHeader}
                // No alert for empty fields, so these props are unused
                secretAnswerRef={null}
                invalidFields={null}
                onInvalidField={() => {}}
            />
            <FormLabel>
                <TranslatedSpan english={'secret_question'}/>
            </FormLabel>

            <Div>{secretQuestion}</Div>
            <TextField
                errorMessage={getSecretAnswerErrMessage()}
                label={translate('secret_answer')}
                name={'secretAnswer'}
                onBlur={() => setSecretAnswerTouched(true)}
                onChange={onSecretAnswerChange}
                onClick={formStart}
                value={secretAnswer}
            />
            <Buttons
                backText={translate('back')}
                forwardText={translate('next')}
                isLoading={answerQuestionLoading}
                onBackClick={props.onBackClick ? props.onBackClick : prev}
                onForwardClick={secretQuestionSubmit}
            />
        </Step>
    );
}